import type { AxiosProgressEvent } from "axios";
import type { HandledApiResponse } from "@entrilia/shared/api/axiosHelper";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import { logError } from "@entrilia/shared/logging";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import adminApi from "../../../../../api/adminApi";
import blobStorageApi from "../../../../../api/blobStorageApi";

const uploadFileToBlobStorage = withErrorHandling(blobStorageApi.uploadFileToBlobStorage);

const getUploadZipUrlForDataImportFileContainer = withErrorHandling(adminApi.getUploadZipUrlForDataImportFileContainer);
const startUnzipForDataImportFileContainer = withErrorHandling(adminApi.startUnzipForDataImportFileContainer);
const getUnzipJobProgressForDataImportFileContainer = withErrorHandling(
  adminApi.getUnzipJobProgressForDataImportFileContainer
);

const uploadZipFileIntoDataImportFileContainer = async (
  id: string,
  file: File,
  onUploadProgress?: (e: AxiosProgressEvent) => void
): Promise<HandledApiResponse<null>> => {
  const [fileToken, fileTokenError] = await getUploadZipUrlForDataImportFileContainer(id);
  if (fileTokenError) {
    logError(fileTokenError, "[zipUploaderService] getUploadZipUrlForDataImportFileContainer");
    return [undefined, fileTokenError];
  }

  const [, uploadError] = await uploadFileToBlobStorage(fileToken.token, file, (e: AxiosProgressEvent) => {
    if (onUploadProgress && e.progress) {
      onUploadProgress({ ...e, progress: e.progress * 0.9 }); // estimate 90% of the progress for upload, and 10% for unzip
    }
  });

  if (uploadError) {
    logError(uploadError, "[zipUploaderService] uploadFileToAzureBlobStorage");
    return [undefined, uploadError];
  }

  const [jobStartResult, jobStartError] = await startUnzipForDataImportFileContainer(id, fileToken);
  if (jobStartError) {
    logError(jobStartError, "[zipUploaderService] startUnzipForDataImportFileContainer");
    return [undefined, jobStartError];
  }

  const veryRoughEstimatedDocumentCount = file.size / 10_000_000; // jobProgress.progressPercent is unavailable for this job (always 0); just to make the progress bar move
  const delayMs = 2000;
  let attemptsLeftBeforeTimeout = 15; // 30 seconds timeout when no unzip progress has been made
  let lastCurrentProgressItem = 0;

  while (attemptsLeftBeforeTimeout > 0) {
    await wait(delayMs);

    const [jobProgress, jobProgressError] = await getUnzipJobProgressForDataImportFileContainer(
      id,
      jobStartResult.operationId
    );

    if (jobProgressError) {
      logError(jobProgressError, "[zipUploaderService] getUnzipJobProgressForDataImportFileContainer");
      return [undefined, jobProgressError];
    }

    if (jobProgress.finished) {
      if (jobProgress.isSuccess) {
        return [null, undefined];
      }

      const unzipJobError: ApiError = {
        message: jobProgress.errorMessage || "Unzip job failed",
        type: "General",
      };
      return [undefined, unzipJobError];
    }

    attemptsLeftBeforeTimeout =
      jobProgress.currentProgressItem > lastCurrentProgressItem ? 15 : attemptsLeftBeforeTimeout - 1;

    lastCurrentProgressItem = jobProgress.currentProgressItem;

    onUploadProgress?.({
      progress: 0.9 + 0.1 * Math.min(0.99, jobProgress.currentProgressItem / veryRoughEstimatedDocumentCount), // remaining 10% of the progress for unzip
      loaded: jobProgress.currentProgressItem,
      bytes: 0,
      lengthComputable: false,
    });
  }

  const timeoutError: ApiError = {
    message: "Unzip job timed out",
    type: "General",
  };

  logError(timeoutError, "[zipUploaderService] getUnzipJobProgressForDataImportFileContainer");
  return [undefined, timeoutError];
};

const zipUploaderService = {
  uploadZipFileIntoDataImportFileContainer,
};

export default zipUploaderService;
